import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Download, ChevronDown, ChevronRight, X } from "lucide-react";

const input = "w-full bg-white border hairline px-3 py-2 text-sm focus:outline-none focus:border-[color:var(--brand-primary)]";
const money = (v) => `A$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const ProductForm = ({ initial, onClose, onSaved }) => {
  const editing = Boolean(initial?.id);
  const [f, setF] = useState({
    name: initial?.name || "",
    commission_type: initial?.commission_type || "percent",
    rate: initial?.rate ?? "",
    active: initial?.active !== false,
  });
  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...f, rate: Number(f.rate) };
      if (editing) await api.put(`/commissions/products/${initial.id}`, payload);
      else await api.post("/commissions/products", payload);
      toast.success(editing ? "Product line updated" : "Product line created");
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
  };
  return (
    <form onSubmit={submit} className="border-2 border-[color:var(--brand-primary)] bg-white p-5 space-y-3" data-testid="product-form">
      <div className="flex items-center justify-between">
        <div className="label-eyebrow text-[var(--brand-primary)]">{editing ? `Edit ${initial.name}` : "New product line"}</div>
        <button type="button" onClick={onClose} className="text-neutral-400 hover:text-black"><X size={15} /></button>
      </div>
      <div className="grid sm:grid-cols-4 gap-3">
        <input className={input} placeholder="Product line name *" required value={f.name}
               onChange={e => setF(v => ({ ...v, name: e.target.value }))} data-testid="product-name" />
        <select className={input} value={f.commission_type}
                onChange={e => setF(v => ({ ...v, commission_type: e.target.value }))} data-testid="product-type">
          <option value="percent">% of sale value</option>
          <option value="flat">Flat A$ per sale</option>
        </select>
        <input type="number" step="0.01" min="0.01" className={input} required data-testid="product-rate"
               placeholder={f.commission_type === "percent" ? "e.g. 10 (%)" : "e.g. 500 (A$)"}
               value={f.rate} onChange={e => setF(v => ({ ...v, rate: e.target.value }))} />
        <label className="flex items-center gap-2 text-sm text-neutral-600">
          <input type="checkbox" checked={f.active} onChange={e => setF(v => ({ ...v, active: e.target.checked }))} data-testid="product-active" /> Active
        </label>
      </div>
      <div className="flex gap-2">
        <button className="btn-primary text-sm" data-testid="product-save">{editing ? "Save" : "Create"}</button>
        <button type="button" className="btn-ghost text-sm" onClick={onClose}>Cancel</button>
      </div>
    </form>
  );
};

export default function CommissionsTab() {
  const [products, setProducts] = useState([]);
  const [team, setTeam] = useState(null);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [formProduct, setFormProduct] = useState(undefined);
  const [expanded, setExpanded] = useState({});
  const reqId = useRef(0);

  const load = () => {
    const id = ++reqId.current;
    api.get("/commissions/config").then(r => { if (id === reqId.current) setProducts(r.data.products); });
    api.get("/commissions/team", { params: { month } }).then(r => { if (id === reqId.current) setTeam(r.data); });
  };
  useEffect(load, [month]);

  const delProduct = async (p) => {
    if (!window.confirm(`Delete product line "${p.name}"?`)) return;
    try {
      await api.delete(`/commissions/products/${p.id}`);
      toast.success("Deleted");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
  };

  const togglePaid = async (d) => {
    try {
      await api.post(`/commissions/deals/${d.id}/paid`, { paid: !d.paid_out });
      toast.success(d.paid_out ? "Payment unmarked" : "Marked as paid out");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
  };

  const exportAll = async () => {
    const r = await api.get("/commissions/team/export", { params: { month }, responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = `team-commissions-${month}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-10" data-testid="admin-commissions-tab">
      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="label-eyebrow">Product lines &amp; rates</div>
          <button className="btn-primary text-sm flex items-center gap-2" onClick={() => setFormProduct(null)} data-testid="add-product-btn">
            <Plus size={13} /> New product line
          </button>
        </div>
        {formProduct !== undefined && (
          <ProductForm initial={formProduct} onClose={() => setFormProduct(undefined)}
                       onSaved={() => { setFormProduct(undefined); load(); }} />
        )}
        <div className="border hairline bg-white">
          <table className="w-full text-sm" data-testid="products-table">
            <thead className="bg-[color:var(--brand-surface2)]">
              <tr className="text-left label-eyebrow"><th className="p-3">Product line</th><th className="p-3">Commission</th><th className="p-3">Status</th><th className="p-3">Actions</th></tr>
            </thead>
            <tbody>
              {products.length === 0 && <tr><td colSpan={4} className="p-5 text-center text-neutral-500" data-testid="products-empty">No product lines yet — agents can't log deals until you add one.</td></tr>}
              {products.map(p => (
                <tr key={p.id} className={`border-t hairline ${p.active === false ? "opacity-50" : ""}`} data-testid={`product-row-${p.id}`}>
                  <td className="p-3 font-medium">{p.name}</td>
                  <td className="p-3">{p.commission_type === "percent" ? `${p.rate}% of sale value` : `${money(p.rate)} flat per sale`}</td>
                  <td className="p-3"><span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 ${p.active === false ? "bg-neutral-200 text-neutral-500" : "bg-[#0B3D2E] text-white"}`}>{p.active === false ? "inactive" : "active"}</span></td>
                  <td className="p-3">
                    <div className="flex gap-1">
                      <button className="btn-ghost text-xs px-2 py-1" onClick={() => setFormProduct(p)} data-testid={`product-edit-${p.id}`}><Pencil size={11} /></button>
                      <button className="text-xs px-2 py-1 text-[#8B2E2E] hover:bg-[#8B2E2E]/10" onClick={() => delProduct(p)} data-testid={`product-delete-${p.id}`}><Trash2 size={11} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="label-eyebrow">Agent commissions · read-only</div>
          <div className="flex items-center gap-2">
            <input type="month" className={input} value={month} onChange={e => setMonth(e.target.value)} data-testid="team-month" />
            <button className="btn-ghost text-sm flex items-center gap-2" onClick={exportAll} data-testid="team-export"><Download size={13} /> Export all</button>
          </div>
        </div>
        {team && (
          <>
            <div className="flex gap-6 text-sm text-neutral-600" data-testid="team-totals">
              <span>Team earned {team.month}: <strong className="font-display text-[var(--brand-primary)]">{money(team.totals.earned_aud)}</strong></span>
              <span>Pipeline commission: <strong className="font-display">{money(team.totals.pipeline_commission_aud)}</strong></span>
            </div>
            <div className="space-y-2">
              {team.agents.length === 0 && <div className="border hairline bg-white p-5 text-sm text-neutral-500" data-testid="team-empty">No agent deals logged yet.</div>}
              {team.agents.map(a => (
                <div key={a.user_id} className="border hairline bg-white" data-testid={`agent-block-${a.user_id}`}>
                  <button className="w-full p-4 flex items-center justify-between text-left"
                          onClick={() => setExpanded(e => ({ ...e, [a.user_id]: !e[a.user_id] }))} data-testid={`agent-toggle-${a.user_id}`}>
                    <span className="flex items-center gap-2 font-medium">
                      {expanded[a.user_id] ? <ChevronDown size={13} /> : <ChevronRight size={13} />}{a.name}
                    </span>
                    <span className="flex gap-6 text-sm">
                      <span className="text-neutral-500">{a.deals.length} deal{a.deals.length !== 1 ? "s" : ""}</span>
                      <span>Earned: <strong className="text-[var(--brand-primary)]">{money(a.summary.earned_aud)}</strong></span>
                      <span className="text-neutral-500">Pipeline: {money(a.summary.pipeline_commission_aud)}</span>
                    </span>
                  </button>
                  {expanded[a.user_id] && (
                    <table className="w-full text-sm border-t hairline">
                      <thead className="bg-[color:var(--brand-surface2)]">
                        <tr className="text-left label-eyebrow"><th className="p-2 pl-4">Client</th><th className="p-2">Product</th><th className="p-2">Sale</th><th className="p-2">Status</th><th className="p-2">Commission (AUD)</th><th className="p-2">Collected</th><th className="p-2">Payment</th></tr>
                      </thead>
                      <tbody>
                        {a.deals.map(d => (
                          <tr key={d.id} className="border-t hairline" data-testid={`team-deal-${d.id}`}>
                            <td className="p-2 pl-4">{d.client_name}</td>
                            <td className="p-2 text-neutral-600">{d.product_name}</td>
                            <td className="p-2 font-mono text-xs">{d.sale_value.toLocaleString()} {d.currency}</td>
                            <td className="p-2 text-xs uppercase tracking-widest text-neutral-500">{d.status.replace("_", " ")}</td>
                            <td className="p-2 font-display">{money(d.commission_aud)}</td>
                            <td className="p-2 font-mono text-xs text-neutral-400">{(d.cash_collected_at || "").slice(0, 10) || "—"}</td>
                            <td className="p-2">
                              {d.status !== "cash_collected" ? (
                                <span className="text-xs text-neutral-300">—</span>
                              ) : d.paid_out ? (
                                <button onClick={() => togglePaid(d)} title={`Paid ${(d.paid_at || "").slice(0, 10)} by ${d.paid_by || ""} — click to undo`}
                                        className="text-[10px] uppercase tracking-widest px-2 py-0.5 bg-[#0B3D2E] text-white"
                                        data-testid={`team-paid-toggle-${d.id}`}>
                                  Paid ✓
                                </button>
                              ) : (
                                <button onClick={() => togglePaid(d)}
                                        className="text-[10px] uppercase tracking-widest px-2 py-0.5 border hairline text-neutral-600 hover:border-[color:var(--brand-primary)] hover:text-[color:var(--brand-primary)]"
                                        data-testid={`team-paid-toggle-${d.id}`}>
                                  Mark paid
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </section>
    </div>
  );
}
